import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './store/authStore';

// Layouts
import MainLayout from './layouts/MainLayout';

// Components
import PrivateRoute from './components/PrivateRoute';
import { Loading } from './components/common';

// Landing Page (NUEVA)
import LandingPage from './pages/LandingPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// Dashboard
import DashboardPage from './pages/dashboard/DashboardPage';

// Usuarios
import UsuariosPage from './pages/usuarios/UsuariosPage';

// Organizaciones
import OrganizacionesPage from './pages/organizaciones/OrganizacionesPage';

// Glosario
import GlosarioPage from './pages/glosario/GlosarioPage';

// Lista de Chequeo
import ChequeoPage from './pages/chequeo/ChequeoPage';

// Encuestas
import EncuestasPage from './pages/encuestas/EncuestasPage';
import PlantillaEncuestaPage from './pages/encuestas/PlantillaEncuestaPage';

// Códigos de Ética
import CodigosPage from './pages/codigos/CodigosPage';

// Comité de Ética
import ComitePage from './pages/comite/ComitePage';

// Denuncias
import DenunciasPage from './pages/denuncias/DenunciasPage';

// Placeholder pages for other modules
const PlaceholderPage = ({ title }) => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-gray-900 mb-4">{title}</h1>
      <p className="text-gray-600">Próximamente...</p>
    </div>
  </div>
);

function App() {
  const { checkAuth, isAuthenticated, isLoading } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return <Loading fullScreen text="Iniciando aplicación..." />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Landing page pública (NUEVA) */}
        <Route
          path="/"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />
          }
        />

        {/* Auth routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          }
        />
        <Route
          path="/register"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />
          }
        />

        {/* Protected routes */}
        <Route
          path="/app"
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          {/* Dashboard */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
        </Route>

        {/* Dashboard shortcut (mantiene /dashboard funcionando) */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<DashboardPage />} />
        </Route>

        {/* Módulos protegidos bajo MainLayout */}
        <Route
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          {/* Usuarios */}
          <Route
            path="usuarios"
            element={
              <PrivateRoute requiredRoles={['superadmin', 'admin_org']}>
                <UsuariosPage />
              </PrivateRoute>
            }
          />

          {/* Organizaciones */}
          <Route
            path="organizaciones"
            element={
              <PrivateRoute requiredRoles={['superadmin']}>
                <OrganizacionesPage />
              </PrivateRoute>
            }
          />

          {/* Glosario */}
          <Route path="glosario" element={<GlosarioPage />} />

          {/* Lista de Chequeo */}
          <Route
            path="chequeo"
            element={
              <PrivateRoute requiredRoles={['superadmin', 'admin_org', 'miembro']}>
                <ChequeoPage />
              </PrivateRoute>
            }
          />

          {/* Encuestas */}
          <Route path="encuestas" element={<EncuestasPage />} />
          <Route
            path="encuestas/plantilla"
            element={
              <PrivateRoute requiredRoles={['superadmin']}>
                <PlantillaEncuestaPage />
              </PrivateRoute>
            }
          />

          {/* Códigos de Ética */}
          <Route
            path="codigos"
            element={
              <PrivateRoute requiredRoles={['superadmin', 'admin_org']}>
                <CodigosPage />
              </PrivateRoute>
            }
          />

          {/* Comité de Ética */}
          <Route
            path="comite"
            element={
              <PrivateRoute requiredRoles={['superadmin', 'admin_org']}>
                <ComitePage />
              </PrivateRoute>
            }
          />

          {/* Denuncias */}
          <Route path="denuncias" element={<DenunciasPage />} />

          {/* Configuración */}
          <Route
            path="configuracion"
            element={<PlaceholderPage title="Configuración" />}
          />
        </Route>

        {/* Catch all - 404 */}
        <Route
          path="*"
          element={
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-xl text-gray-600 mb-8">Página no encontrada</p>
                <a
                  href="/dashboard"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Volver al Dashboard
                </a>
              </div>
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
